import { createContext } from "react";

export type Role = "admin" | "member" | "viewer";

/**
 * Shape of the structured 422 body returned by ``/api/auth/me`` when
 * the Cognito user's custom attributes are malformed (e.g. an operator
 * typed "Sunwave Digital" into ``custom:company_id_v2`` in the AWS
 * console instead of ``co-sunwave-digital``).
 *
 * The backend builds this; the frontend renders ``detail`` and
 * ``remediation`` verbatim. Mirrors ``_claims_error_body`` in
 * ``backend/app/routes/auth.py``.
 */
export interface ProvisioningError {
  /** Human-readable explanation of what's wrong. Shown to the user. */
  detail: string;
  /** Machine code for analytics / branching. Currently:
   *  MALFORMED_COMPANY_ID_CLAIM, MISSING_COMPANY_ID_CLAIM,
   *  INVALID_ROLE_CLAIM, MISSING_ROLE_CLAIM, COMPANY_ID_EMAIL_MISMATCH. */
  error_code: string;
  /** The Cognito attribute that needs fixing (e.g. "company_id" or "role"). */
  field: string;
  /** Operator-facing remediation text. Shown to the user. */
  remediation: string;
}

export interface AuthUser {
  userId: string;
  email: string;
  companyId: string;
  companyName: string;
  role: Role;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  claims: Record<string, unknown> | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True when valid Amplify tokens exist but /api/auth/me returned 503
   * (post-confirmation Lambda still writing DB rows). Soft transient state.
   */
  provisioning: boolean;
  /**
   * True after the provisioning state has persisted past the timeout
   * (15s) OR when /api/auth/me returned a structured 422 telling us
   * provisioning cannot succeed without operator action (malformed
   * Cognito attributes). In both cases the UI shows an error card
   * with a sign-out button so the user is never trapped on the
   * "Setting up your account…" message indefinitely.
   */
  provisioningTimedOut: boolean;
  /**
   * Populated when /api/auth/me returns a structured 422 (malformed
   * Cognito claims). The UI renders ``detail`` and ``remediation``
   * verbatim so the user sees the actual reason instead of a generic
   * "we couldn't set up your account" message. ``null`` for all other
   * states, including 15-second timeouts.
   */
  provisioningError: ProvisioningError | null;
  /**
   * Always null in normal operation. AuthProvider never surfaces raw
   * /api/auth/me errors here — they are classified into ``user=null``
   * (silent) or ``provisioning=true`` (soft status). Kept on the contract
   * for forward compatibility; consumers should not rely on it.
   */
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
